import React, { useState } from 'react';
import { AdVocalizeProvider, useAdVocalizeCore } from './context/AdVocalizeCore';
import { AppShell, AV_T } from './components/AppShell';
import { 
    Star, 
    CheckCircle, 
    RefreshCw, 
    Key, 
    Lock, 
    Mail 
} from 'lucide-react';

import Step0_Upload from './steps/Step0_Upload';
import Step1_CropStudio from './steps/Step1_CropStudio';
import Step2_DirectorConsole from './steps/Step2_DirectorConsole';
import Step3_MasterTape from './steps/Step3_MasterTape';

const V3Auth = () => {
    const { 
        handleGoogleAuth, 
        handleEmailAuth, 
        authError, 
        authMessage, 
        isAuthLoading, 
        redeemPhase, 
        setRedeemPhase,
        pendingCode,
        setPendingCode
    } = useAdVocalizeCore();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [authMode, setAuthMode] = useState('login');

    const handleContinue = () => {
        if (!pendingCode.trim()) return;
        setRedeemPhase('auth');
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center p-6">
            <div className="w-full max-w-md space-y-8 animate-in fade-in zoom-in-95 duration-500">
                {/* Logo & Header */}
                <div className="text-center space-y-4">
                    <div className={`w-16 h-16 ${AV_T.spectrum} rounded-2xl flex items-center justify-center text-white font-black text-3xl mx-auto shadow-2xl shadow-indigo-500/20`}>v</div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full">
                        <Star className="w-3 h-3 text-indigo-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">AppSumo Edition</span>
                    </div>
                    <h2 className="text-3xl font-black tracking-tight text-white">Director's Suite</h2>
                    <p className="text-slate-500 text-sm">Activate your professional studio access [v3.0.0]</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-8 space-y-6 backdrop-blur-3xl shadow-2xl">
                    {redeemPhase === 'code' ? (
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">License Key</label>
                                <div className="relative">
                                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                                    <input 
                                        value={pendingCode}
                                        onChange={(e) => setPendingCode(e.target.value.toUpperCase())}
                                        placeholder="VOCAL-AD-XXXX-XXXX"
                                        className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-2xl text-white font-mono font-bold tracking-widest focus:border-indigo-500 transition-all outline-none"
                                    />
                                </div>
                            </div>
                            
                            <button 
                                onClick={handleContinue}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-indigo-600/20 transition-all active:scale-95"
                            >
                                Verify License →
                            </button>

                            <div className="text-center">
                                <button onClick={() => setRedeemPhase('auth')} className="text-xs font-bold text-slate-500 hover:text-white transition-colors">
                                    Already have an account? Sign In
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {pendingCode && (
                                <div className="flex items-center gap-3 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                                    <CheckCircle className="w-4 h-4 text-indigo-400" />
                                    <span className="text-xs font-bold text-indigo-300 tracking-tight">Redeeming: {pendingCode}</span>
                                </div>
                            )}

                            <button 
                                onClick={handleGoogleAuth}
                                disabled={isAuthLoading}
                                className="w-full py-4 bg-white hover:bg-slate-100 text-slate-900 rounded-2xl font-black text-sm flex items-center justify-center gap-3 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                            >
                                {isAuthLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />}
                                Continue with Google
                            </button>

                            <div className="flex items-center gap-4">
                                <div className="h-px bg-white/10 flex-1" />
                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">or</span>
                                <div className="h-px bg-white/10 flex-1" />
                            </div>

                            <div className="flex bg-white/5 p-1 rounded-xl border border-white/5">
                                <button onClick={() => setAuthMode('signup')} className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${authMode === 'signup' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Join</button>
                                <button onClick={() => setAuthMode('login')} className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${authMode === 'login' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Sign In</button>
                            </div>

                            <form onSubmit={(e) => { e.preventDefault(); handleEmailAuth(email, password, authMode); }} className="space-y-3">
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                                    <input 
                                        type="email" value={email} onChange={e => setEmail(e.target.value)}
                                        placeholder="Email Address"
                                        className="w-full pl-12 pr-4 py-3.5 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:border-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                                    <input 
                                        type="password" value={password} onChange={e => setPassword(e.target.value)}
                                        placeholder="Password"
                                        className="w-full pl-12 pr-4 py-3.5 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:border-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <button 
                                    type="submit" disabled={isAuthLoading}
                                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {isAuthLoading ? "Verifying..." : authMode === 'signup' ? "Activate Access" : "Enter Studio"}
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {(authError || authMessage) && (
                    <div className={`p-4 rounded-2xl text-xs font-bold text-center border animate-in slide-in-from-top-2 ${authError ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'}`}>
                        {authError || authMessage}
                    </div>
                )}
                
                {redeemPhase === 'auth' && (
                    <button onClick={() => setRedeemPhase('code')} className="w-full text-slate-600 hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest">
                        ← Enter different code
                    </button>
                )}
            </div>
        </div>
    );
};

const StudioOrchestrator = () => {
    const { step, user } = useAdVocalizeCore();

    // Only block unauthenticated users — logged-in users always reach the studio
    if (!user) return <V3Auth />;

    return (
        <AppShell
            heading={step === 0 ? "Aperture" : step === 1 ? "Crop Studio" : step === 2 ? "Director Console" : "Master Tape"}
            currentStep={step + 1}
        >
            <div className="animate-in fade-in duration-700">
                {step === 0 && <Step0_Upload />}
                {step === 1 && <Step1_CropStudio />}
                {step === 2 && <Step2_DirectorConsole />}
                {step === 3 && <Step3_MasterTape />}
            </div>
        </AppShell>
    );
};

const AppV3 = () => {
    return (
        <AdVocalizeProvider>
            <StudioOrchestrator />
        </AdVocalizeProvider>
    );
};

export default AppV3;
